import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { Skeleton } from 'components/UnvUI'

import $api from 'http/index'
import DistanceService from 'service/DistanceService'
import Notification from 'utils/Notifications'

import ClassroomRow from './components/ClassroomRow/ClassroomRow'
import CreateClassroomForm from './components/CreateClassroomForm/CreateClassroomForm'

import styles from './VirtualClassroomPage.module.scss'

const splitByDate = (items = []) => {
  const now = Date.now()
  const upcoming = []
  const past = []
  for (const it of items) {
    const ts = new Date(it.scheduled_at || it.start_at || 0).getTime()
    if (ts >= now - 60 * 60 * 1000) upcoming.push(it)
    else past.push(it)
  }
  upcoming.sort(
    (a, b) =>
      new Date(a.scheduled_at || a.start_at || 0) -
      new Date(b.scheduled_at || b.start_at || 0),
  )
  past.sort(
    (a, b) =>
      new Date(b.scheduled_at || b.start_at || 0) -
      new Date(a.scheduled_at || a.start_at || 0),
  )
  return { upcoming, past }
}

const VirtualClassroomPage = () => {
  const { t } = useTranslation('common')

  const [classrooms, setClassrooms] = useState([])
  const [me, setMe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [joiningId, setJoiningId] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false })

  const isTeacher = useMemo(() => {
    if (!me) return false
    const roles = me.roles || []
    return (
      roles.includes('teacher') ||
      me.primary_role === 'teacher' ||
      roles.includes('lecturer') ||
      !!me.is_staff ||
      !!me.is_superuser
    )
  }, [me])

  const load = async () => {
    setLoading(true)
    try {
      const res = await DistanceService.listClassrooms()
      setClassrooms(res.data?.results || res.data || [])
    } catch {
      setClassrooms([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    ;(async () => {
      try {
        const r = await $api.get('/admin-panel/me/')
        setMe(r.data)
      } catch {
        setMe(null)
      }
    })()
  }, [])

  const handleJoin = async (classroom) => {
    setJoiningId(classroom.id)
    try {
      const res = await DistanceService.joinClassroom(classroom.id)
      const url = res.data?.join_url
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        setNotify({
          isOpen: true,
          message: 'Ссылка для подключения недоступна',
          type: 'error',
        })
      }
    } catch (e) {
      setNotify({
        isOpen: true,
        message: e.response?.data?.detail || 'Не удалось присоединиться',
        type: 'error',
      })
    } finally {
      setJoiningId(null)
    }
  }

  const handleCreate = async (payload) => {
    setCreating(true)
    setCreateError('')
    try {
      await DistanceService.createClassroom(payload)
      setShowForm(false)
      setNotify({
        isOpen: true,
        message: 'Виртуальный класс создан',
        type: 'success',
      })
      load()
    } catch (e) {
      setCreateError(e.response?.data?.detail || 'Не удалось создать класс')
    } finally {
      setCreating(false)
    }
  }

  const { upcoming, past } = splitByDate(classrooms)

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('auto.distancelearning_virtualclassroompage.k1')}</h1>
          <div className={styles.subtitle}>
            {t('auto.distancelearning_virtualclassroompage.k2')}
          </div>
        </div>
        {isTeacher && (
          <button
            className={styles.toggleBtn}
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? 'Скрыть форму' : 'Создать класс'}
          </button>
        )}
      </div>

      {isTeacher && showForm && (
        <CreateClassroomForm
          onCreate={handleCreate}
          creating={creating}
          error={createError}
        />
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('auto.distancelearning_virtualclassroompage.k3')}</h2>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        ) : upcoming.length === 0 ? (
          <div className={styles.empty}>{t('distance.no_upcoming')}</div>
        ) : (
          <div className={styles.list}>
            {upcoming.map((c) => (
              <ClassroomRow
                key={c.id}
                classroom={c}
                onJoin={handleJoin}
                joining={joiningId === c.id}
              />
            ))}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('auto.distancelearning_virtualclassroompage.k4')}</h2>
        {past.length === 0 ? (
          <div className={styles.empty}>{t('auto.distancelearning_virtualclassroompage.k5')}</div>
        ) : (
          <div className={styles.list}>
            {past.map((c) => (
              <ClassroomRow
                key={c.id}
                classroom={c}
                onJoin={handleJoin}
                joining={joiningId === c.id}
              />
            ))}
          </div>
        )}
      </section>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default VirtualClassroomPage
